import React from 'react';
import { Link } from 'react-router-dom';
import { ClompApi } from '../api/ClompApi';
import { endSession } from '../api/session';
import './BingoPage.css';

// The Old School RuneScape bingo event. This page stands apart from the rest of the site: it has no navbar, and
// nothing links to it, so it is reached only by its own address. Anyone with the address can look; Admins and
// Founders (logged in with their clomp account, from the bar at the top) can edit the teams, tiles and timer and mark
// tiles as completed.

const api = new ClompApi();

// The most rows, and the most columns, the board can have
const SIDE_MAX = 10;
const TITLE_MAX = 80;
const POINTS_MAX = 1000;
const IMAGE_MAX = 500;
const TEAM_NAME_MAX = 40;
// The colours teams are drawn in until one is chosen (the backend's BINGO_TEAM_COLORS): a new team's picker starts on
// the next of these
const TEAM_COLORS = ['#4a90e2', '#d9443a', '#56a85a', '#e0b13a', '#9b6bd6', '#e07b39', '#3bb3ad', '#d65c9c'];
// How often an open page checks for changes made by someone else
const REFRESH_SECONDS = 20;

// A team's score: the points of the tiles it has completed
function teamScore(team, tiles) {
    return team.completed.reduce((sum, tile) => sum + (tiles[tile] ? tiles[tile].points : 0), 0);
}

const twoDigits = (number) => String(number).padStart(2, '0');

// A date as text (as the backend gives it) in the form a date-and-time input holds, in the viewer's own time zone
function toInputTime(date) {
    if (!date) {
        return '';
    }
    let d = new Date(date);
    return `${d.getFullYear()}-${twoDigits(d.getMonth() + 1)}-${twoDigits(d.getDate())}T${twoDigits(d.getHours())}:${twoDigits(d.getMinutes())}`;
}

// The event's timer: a countdown to the start, then to the end, then a note that it is over above a clock at zero. Shows nothing until
// both times are set. It keeps its own clock, so the rest of the page isn't drawn again every second.
class BingoTimer extends React.Component {

    state = { now: Date.now() };

    componentDidMount() {
        this.timer = setInterval(() => this.setState({ now: Date.now() }), 1000);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    render() {
        let { startsAt, endsAt } = this.props;
        if (!startsAt || !endsAt) {
            return null;
        }
        let start = new Date(startsAt).getTime();
        let end = new Date(endsAt).getTime();
        let now = this.state.now;
        // once the event is over the clock stays at zero
        let finished = now >= end;
        let seconds = finished ? 0 : Math.ceil(((now < start ? start : end) - now) / 1000);
        let parts = [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60];
        return (
            <div className="bingo-timer" role="timer">
                {finished ?
                    <span className="bingo-timer-finished">The bingo is finished</span>
                    :
                    <span className="bingo-timer-label">{now < start ? 'Bingo starts in' : 'Time remaining'}</span>}
                <span className="bingo-timer-digits">
                    <span className="bingo-timer-clock">{parts.map(twoDigits).join(':')}</span>
                    <span className="bingo-timer-units" aria-hidden="true"><span>days</span><span>hrs</span><span>min</span><span>sec</span></span>
                </span>
            </div>
        );
    }

}

export class BingoPage extends React.Component {

    state = {
        // The board is rows x columns tiles, held in order from the top left, row by row
        rows: 0,
        columns: 0,
        tiles: [],
        // When the event starts and ends, as the backend gives them: both null until they are set
        startsAt: null,
        endsAt: null,
        // Staff: the form for those times, and what its two boxes say
        editingTimer: false,
        timerStart: '',
        timerEnd: '',
        timerError: null,
        teams: [],
        loading: true,
        error: false,
        // Admins and Founders can change the event from this page
        canEdit: false,
        // The clomp account logged in on this browser ({ username }), null if none; checked tells whether that is
        // known yet
        account: null,
        accountChecked: false,
        // The id of the team whose completed tiles the board is showing
        selected: null,
        editingTeams: false,
        // What each team's name box and colour say while the teams are being edited, by team id
        names: {},
        colors: {},
        newTeam: '',
        // The colour picked for a new team, or null for the next of TEAM_COLORS
        newTeamColor: null,
        // The id of the team whose Remove button has been pressed and is waiting to be confirmed
        confirmingRemove: null,
        editingTiles: false,
        // The row or column whose remove button has been pressed and is waiting to be confirmed:
        // { kind: 'row' or 'column', index }, or null
        confirmingLine: null,
        // The number of the tile whose form is open, or null
        tileOpen: null,
        tileTitle: '',
        tilePoints: '1',
        tileImage: '',
        // Looking up a picture on the Old School RuneScape Wiki: what has been typed, and the pages found for it
        // (null before anything has been looked up)
        wikiText: '',
        wikiResults: null,
        wikiSearching: false,
        wikiError: false,
        saving: false,
        // Something that went wrong with the last change, shown by whichever part it was made in
        teamError: null,
        boardError: null,
        tileError: null
    };

    // Counts the requests sent, so an answer is only shown if nothing newer has been asked since
    requests = 0;
    // The same, for looking things up on the wiki
    wikiRequests = 0;

    componentDidMount() {
        this.mounted = true;
        this.oldTitle = document.title;
        document.title = 'Frog Bingo';
        // keep the page out of search results
        this.robots = document.createElement('meta');
        this.robots.name = 'robots';
        this.robots.content = 'noindex';
        document.head.appendChild(this.robots);

        this.load();
        this.timer = setInterval(() => {
            if (!document.hidden && !this.state.saving) {
                this.load();
            }
        }, REFRESH_SECONDS * 1000);

        this.checkAccount();
    }

    // Who is logged in here, and whether they may edit the event
    checkAccount() {
        if (localStorage.getItem('token') === null) {
            this.setState({ account: null, canEdit: false, accountChecked: true });
            return;
        }
        Promise.all([api.auth.getSession(), api.admin.getAccess()]).then(([session, access]) => {
            if (this.mounted) {
                this.setState({
                    account: session.loggedIn ? { username: session.username } : null,
                    canEdit: Boolean(session.loggedIn && access.admin),
                    accountChecked: true
                });
            }
        }).catch(() => {
            if (this.mounted) {
                this.setState({ accountChecked: true });
            }
        });
    }

    logOut = () => {
        let token = localStorage.getItem('token');
        api.auth.logout(token).catch(() => {});
        endSession(false);
        this.setState({ account: null, canEdit: false, editingTeams: false, editingTiles: false, editingTimer: false, tileOpen: null });
    }

    componentWillUnmount() {
        this.mounted = false;
        clearInterval(this.timer);
        document.title = this.oldTitle;
        this.robots.remove();
    }

    // Show the event as the backend gave it, unless something newer has been asked for since
    show(request, board) {
        if (!this.mounted || request !== this.requests) {
            return;
        }
        let selected = this.state.selected;
        if (!board.teams.some((team) => team.id === selected)) {
            // nothing picked yet, or the picked team has been removed: show the first team
            let scored = this.scored(board.teams, board.tiles);
            selected = scored.length > 0 ? scored[0].id : null;
        }
        this.setState({ rows: board.rows, columns: board.columns, startsAt: board.startsAt || null, endsAt: board.endsAt || null, tiles: board.tiles, teams: board.teams, selected, loading: false, error: false });
    }

    load() {
        let request = ++this.requests;
        api.bingo.getBoard().then((board) => this.show(request, board)).catch((err) => {
            console.log('Error loading bingo: ', err);
            if (this.mounted && this.state.loading) {
                this.setState({ loading: false, error: true });
            }
        });
    }

    // Send a change and show what comes back. errorKey is the state the message goes in if it fails. Resolves to
    // whether it worked.
    change(send, errorKey) {
        let request = ++this.requests;
        this.setState({ saving: true, [errorKey]: null });
        return send().then((board) => {
            this.show(request, board);
            if (this.mounted) {
                this.setState({ saving: false });
            }
            return true;
        }).catch((err) => {
            if (!this.mounted) {
                return false;
            }
            let status = err.response && err.response.status;
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({
                saving: false,
                [errorKey]: message || "Couldn't save that. Please try again.",
                // the login ran out, or isn't an admin one any more
                canEdit: status === 401 || status === 403 ? false : this.state.canEdit
            });
            // whatever was shown ahead of the answer may be wrong now
            this.load();
            return false;
        });
    }

    // Teams with their scores, in the order they were added: [{ id, name, completed, score }]
    scored(teams = this.state.teams, tiles = this.state.tiles) {
        return teams.map((team) => ({ ...team, score: teamScore(team, tiles) })).sort((a, b) => a.id - b.id);
    }

    // The colour a new team's picker shows: the one picked, or the next of the defaults
    newTeamColor() {
        return this.state.newTeamColor || TEAM_COLORS[this.state.teams.length % TEAM_COLORS.length];
    }

    addTeam = (e) => {
        e.preventDefault();
        let name = this.state.newTeam.trim();
        if (!name) {
            return;
        }
        this.change(() => api.bingo.addTeam(name, this.newTeamColor()), 'teamError').then((added) => {
            if (added && this.mounted) {
                this.setState({ newTeam: '', newTeamColor: null });
            }
        });
    }

    // What has been changed of a team in the editor: { name?, color? }, empty if nothing
    teamChanges(team) {
        let changes = {};
        let name = this.state.names[team.id];
        if (name !== undefined && name.trim() && name.trim() !== team.name) {
            changes.name = name.trim();
        }
        let color = this.state.colors[team.id];
        if (color !== undefined && color !== team.color) {
            changes.color = color;
        }
        return changes;
    }

    saveTeam(team) {
        let changes = this.teamChanges(team);
        if (Object.keys(changes).length === 0) {
            return;
        }
        this.change(() => api.bingo.updateTeam(team.id, changes), 'teamError').then((saved) => {
            if (saved && this.mounted) {
                let names = { ...this.state.names };
                let colors = { ...this.state.colors };
                delete names[team.id];
                delete colors[team.id];
                this.setState({ names, colors });
            }
        });
    }

    removeTeam(team) {
        this.change(() => api.bingo.deleteTeam(team.id), 'teamError').then(() => {
            if (this.mounted) {
                this.setState({ confirmingRemove: null });
            }
        });
    }

    // A tile was clicked: open its form while the tiles are being edited, otherwise mark it for the picked team
    clickTile(tile) {
        if (this.state.editingTiles) {
            let { title, points, image } = this.state.tiles[tile];
            this.wikiRequests++;
            this.setState({
                tileOpen: tile, tileTitle: title, tilePoints: String(points), tileImage: image, tileError: null,
                wikiText: '', wikiResults: null, wikiSearching: false, wikiError: false
            });
            return;
        }
        let team = this.state.teams.find((other) => other.id === this.state.selected);
        if (!team) {
            return;
        }
        let completed = !team.completed.includes(tile);
        // shown straight away, ahead of the backend's answer
        this.setState({
            teams: this.state.teams.map((other) => other.id !== team.id ? other : {
                ...other,
                completed: completed ? [...other.completed, tile] : other.completed.filter((done) => done !== tile)
            })
        });
        this.change(() => api.bingo.setCompleted(team.id, tile, completed), 'boardError');
    }

    // Whether a row or column has anything to lose: a tile that says or shows something, or one a team has completed
    lineInUse(kind, index) {
        let { tiles, teams, columns } = this.state;
        let inLine = (tile) => (kind === 'row' ? Math.floor(tile / columns) : tile % columns) === index;
        return tiles.some((tile, i) => inLine(i) && (tile.title || tile.image)) || teams.some((team) => team.completed.some(inLine));
    }

    // Take a row or column off the board, asking first if that would lose anything
    removeLine(kind, index, confirmed) {
        if (!confirmed && this.lineInUse(kind, index)) {
            this.setState({ confirmingLine: { kind, index }, boardError: null });
            return;
        }
        this.change(() => kind === 'row' ? api.bingo.removeRow(index) : api.bingo.removeColumn(index), 'boardError').then(() => {
            if (this.mounted) {
                this.setState({ confirmingLine: null });
            }
        });
    }

    saveTimer = (e) => {
        e.preventDefault();
        let start = new Date(this.state.timerStart);
        let end = new Date(this.state.timerEnd);
        if (isNaN(start) || isNaN(end)) {
            this.setState({ timerError: 'Pick both a start and an end.' });
            return;
        }
        if (end <= start) {
            this.setState({ timerError: 'The end must be after the start.' });
            return;
        }
        this.changeTimer(start.toISOString(), end.toISOString());
    }

    // Set the event's times (or clear them with nulls) and close their form
    changeTimer(startsAt, endsAt) {
        this.change(() => api.bingo.setTimes(startsAt, endsAt), 'timerError').then((saved) => {
            if (saved && this.mounted) {
                this.setState({ editingTimer: false });
            }
        });
    }

    // The timer beside the page's name, and for staff the button and form that set it
    renderTimer() {
        let { canEdit, startsAt, endsAt, editingTimer, timerStart, timerEnd, timerError, saving } = this.state;
        let set = Boolean(startsAt && endsAt);
        if (!canEdit && !set) {
            return null;
        }
        return (
            <div className="bingo-timer-area">
                <BingoTimer startsAt={startsAt} endsAt={endsAt} />
                {canEdit && !editingTimer ?
                    <button type="button" className="bingo-btn bingo-btn-ghost"
                        onClick={() => this.setState({ editingTimer: true, timerStart: toInputTime(startsAt), timerEnd: toInputTime(endsAt), timerError: null })}>
                        {set ? 'Edit timer' : 'Set timer'}
                    </button>
                    : null}
                {canEdit && editingTimer ?
                    <form className="bingo-timer-form" onSubmit={this.saveTimer}>
                        <label htmlFor="bingo-timer-start">Starts</label>
                        <input id="bingo-timer-start" type="datetime-local" required value={timerStart} onChange={(e) => this.setState({ timerStart: e.target.value })} />
                        <label htmlFor="bingo-timer-end">Ends</label>
                        <input id="bingo-timer-end" type="datetime-local" required min={timerStart || undefined} value={timerEnd} onChange={(e) => this.setState({ timerEnd: e.target.value })} />
                        <p className="bingo-hint">Times are in your own time zone. Everyone sees the same countdown.</p>
                        <div className="bingo-timer-form-actions">
                            {set ? <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={() => this.changeTimer(null, null)}>Remove timer</button> : null}
                            <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={() => this.setState({ editingTimer: false })}>Cancel</button>
                            <button type="submit" className="bingo-btn bingo-btn-primary" disabled={saving}>Save</button>
                        </div>
                        {timerError ? <p className="bingo-error" role="alert">{timerError}</p> : null}
                    </form>
                    : null}
            </div>
        );
    }

    saveTile = (e) => {
        e.preventDefault();
        let { tileOpen, tileTitle, tilePoints, tileImage } = this.state;
        let points = Number(tilePoints);
        if (tilePoints.trim() === '' || !Number.isInteger(points) || points < 0 || points > POINTS_MAX) {
            this.setState({ tileError: `Points must be a whole number from 0 to ${POINTS_MAX}` });
            return;
        }
        this.change(() => api.bingo.setTile(tileOpen, tileTitle.trim(), points, tileImage.trim()), 'tileError').then((saved) => {
            if (saved && this.mounted) {
                this.setState({ tileOpen: null });
            }
        });
    }

    // Look up the pictures of wiki pages whose names start with what has been typed
    searchWiki = () => {
        let text = this.state.wikiText.trim();
        if (!text) {
            return;
        }
        let request = ++this.wikiRequests;
        this.setState({ wikiSearching: true, wikiError: false });
        api.bingo.searchWiki(text).then((results) => {
            if (this.mounted && request === this.wikiRequests) {
                this.setState({ wikiResults: results, wikiSearching: false });
            }
        }).catch(() => {
            if (this.mounted && request === this.wikiRequests) {
                this.setState({ wikiSearching: false, wikiError: true });
            }
        });
    }

    // Use a wiki page's picture for the open tile, and its name as the task if none has been written yet
    pickWiki(page) {
        this.setState({ tileImage: page.image, tileTitle: this.state.tileTitle.trim() ? this.state.tileTitle : page.title });
    }

    // The part of a tile's form for its picture: looking one up on the wiki, or pasting a link to any other
    renderImagePicker() {
        let { tileImage, wikiText, wikiResults, wikiSearching, wikiError } = this.state;
        return (
            <>
                <label htmlFor="bingo-wiki-text">Image</label>
                <div className="bingo-wiki-search">
                    <input id="bingo-wiki-text" type="text" placeholder="Search the OSRS Wiki, e.g. dragon pick" value={wikiText}
                        onChange={(e) => this.setState({ wikiText: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.searchWiki(); } }} />
                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={wikiSearching || !wikiText.trim()} onClick={this.searchWiki}>
                        {wikiSearching ? 'Searching...' : 'Search'}
                    </button>
                </div>
                {wikiError ? <p className="bingo-error" role="alert">Couldn't reach the wiki. Try again, or paste a link below.</p> : null}
                {wikiResults !== null && wikiResults.length === 0 ? <p className="bingo-hint">Nothing on the wiki starts with that. Try the start of the page's name.</p> : null}
                {wikiResults !== null && wikiResults.length > 0 ?
                    <ul className="bingo-wiki-results" aria-label="Wiki pages found">
                        {wikiResults.map((page) => (
                            <li key={page.title}>
                                <button type="button" className={page.image === tileImage ? 'bingo-wiki-picked' : ''} aria-pressed={page.image === tileImage}
                                    title={page.title} onClick={() => this.pickWiki(page)}>
                                    <img src={page.image} alt="" />
                                    <span>{page.title}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    : null}
                <div className="bingo-image-link">
                    {tileImage.trim() ? <img src={tileImage.trim()} alt="Chosen for this tile" /> : null}
                    <input type="text" aria-label="Image link" placeholder="or paste an image link (https://...)" maxLength={IMAGE_MAX} value={tileImage}
                        onChange={(e) => this.setState({ tileImage: e.target.value })} />
                    {tileImage ? <button type="button" className="bingo-btn bingo-btn-ghost" onClick={() => this.setState({ tileImage: '' })}>Remove</button> : null}
                </div>
            </>
        );
    }

    renderTeamEditor(scored) {
        let { names, colors, newTeam, confirmingRemove, saving, teamError } = this.state;
        return (
            <div className="bingo-team-editor">
                {scored.length > 0 ?
                    <ul>
                        {scored.map((team) => {
                            let name = names[team.id] !== undefined ? names[team.id] : team.name;
                            return confirmingRemove === team.id ?
                                <li key={team.id} role="group" aria-label={`Remove ${team.name}`}>
                                    <span className="bingo-confirm">Remove {team.name} and its completed tiles?</span>
                                    <button type="button" className="bingo-btn bingo-btn-danger" disabled={saving} onClick={() => this.removeTeam(team)}>Yes, remove</button>
                                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} autoFocus onClick={() => this.setState({ confirmingRemove: null })}>Keep it</button>
                                </li>
                                :
                                <li key={team.id}>
                                    <input type="color" className="bingo-color" aria-label={`Colour of ${team.name}`} title="Team colour"
                                        value={colors[team.id] || team.color} onChange={(e) => this.setState({ colors: { ...colors, [team.id]: e.target.value } })} />
                                    <input type="text" aria-label={`Name of ${team.name}`} maxLength={TEAM_NAME_MAX} value={name}
                                        onChange={(e) => this.setState({ names: { ...names, [team.id]: e.target.value } })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { this.saveTeam(team); } }} />
                                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving || !name.trim() || Object.keys(this.teamChanges(team)).length === 0}
                                        onClick={() => this.saveTeam(team)}>Save</button>
                                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={() => this.setState({ confirmingRemove: team.id, teamError: null })}>Remove</button>
                                </li>;
                        })}
                    </ul>
                    : null}
                <form onSubmit={this.addTeam}>
                    <input type="color" className="bingo-color" aria-label="Colour of the new team" title="Team colour" value={this.newTeamColor()}
                        onChange={(e) => this.setState({ newTeamColor: e.target.value })} />
                    <input type="text" aria-label="New team name" placeholder="New team name" maxLength={TEAM_NAME_MAX} value={newTeam}
                        onChange={(e) => this.setState({ newTeam: e.target.value })} />
                    <button type="submit" className="bingo-btn bingo-btn-primary" disabled={saving || !newTeam.trim()}>Add team</button>
                </form>
                {teamError ? <p className="bingo-error" role="alert">{teamError}</p> : null}
            </div>
        );
    }

    renderScores(scored) {
        let { canEdit, editingTeams, selected, tiles } = this.state;
        let top = Math.max(0, ...scored.map((team) => team.score));
        let leaders = scored.filter((team) => team.score === top).length;
        let team = scored.find((other) => other.id === selected);
        return (
            <>
                <section className="bingo-scores" aria-label="Scoreboard">
                    <div className="bingo-panel-head">
                        <h2>Teams <span>· tap to view board</span></h2>
                        {canEdit ?
                            <button type="button" className="bingo-btn bingo-btn-ghost" aria-pressed={editingTeams}
                                onClick={() => this.setState({ editingTeams: !editingTeams, confirmingRemove: null, names: {}, colors: {}, teamError: null })}>
                                {editingTeams ? 'Done' : 'Edit teams'}
                            </button>
                            : null}
                    </div>
                    {scored.length === 0 ?
                        <p className="bingo-status">{canEdit ? 'No teams yet. Use Edit teams to add some.' : 'No teams yet.'}</p>
                        :
                        <ol className="bingo-team-list">
                            {scored.map((other) => {
                                // its place by score (teams level on points share one), and whether it is ahead
                                let rank = scored.filter((rival) => rival.score > other.score).length + 1;
                                let leading = top > 0 && other.score === top;
                                return (
                                    <li key={other.id}>
                                        <button type="button" className={'bingo-team' + (other.id === selected ? ' bingo-team-selected' : '')}
                                            style={{ '--team': other.color }} aria-pressed={other.id === selected}
                                            onClick={() => this.setState({ selected: other.id, boardError: null })}>
                                            <span className="bingo-team-rank">{rank}</span>
                                            <span className="bingo-team-swatch" aria-hidden="true" />
                                            <span className="bingo-team-name">{other.name}</span>
                                            <span className="bingo-team-bar" aria-hidden="true">
                                                <span style={{ width: `${top > 0 ? (other.score / top) * 100 : 0}%` }} />
                                            </span>
                                            <span className="bingo-team-score">{other.score}<span className="sr-only"> points</span></span>
                                            <span className="bingo-team-lead">{leading ? <span>{leaders > 1 ? 'Tied' : 'Leading'}</span> : null}</span>
                                            <span className="bingo-team-arrow" aria-hidden="true">›</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>}
                    {canEdit && editingTeams ? this.renderTeamEditor(scored) : null}
                </section>
                {team ?
                    <div className="bingo-stats" aria-label={`${team.name} so far`}>
                        <div className="bingo-stat">
                            <span>Total points</span>
                            <strong className="bingo-stat-gold">{team.score}</strong>
                        </div>
                        <div className="bingo-stat">
                            <span>Tiles complete</span>
                            <strong>{team.completed.length} <small>/ {tiles.length}</small></strong>
                        </div>
                    </div>
                    : null}
            </>
        );
    }

    renderBoard(scored) {
        let { rows, columns, tiles, canEdit, editingTiles, selected, boardError, confirmingLine, saving } = this.state;
        let team = scored.find((other) => other.id === selected);
        let completed = team ? team.completed : [];
        // points are only worth showing on the tiles when they aren't all the same
        let showPoints = tiles.some((tile) => tile.points !== tiles[0].points);
        let hint = null;
        if (canEdit) {
            hint = editingTiles ? 'Click a tile to change what it says. Use ✕ to remove its row or column.'
                : team ? `Click a tile to mark it completed for ${team.name}, or again to unmark it.`
                : 'Add a team to start marking tiles as completed.';
        }
        return (
            <section className="bingo-board-section" aria-label="Board" style={team ? { '--team': team.color } : undefined}>
                <div className="bingo-section-header">
                    <h2>{team ? <><span className="bingo-board-swatch" aria-hidden="true" />{team.name}'s board</> : 'Board'}</h2>
                    {canEdit ?
                        <button type="button" className="bingo-btn bingo-btn-ghost" aria-pressed={editingTiles}
                            onClick={() => this.setState({ editingTiles: !editingTiles, tileOpen: null, boardError: null, confirmingLine: null })}>
                            {editingTiles ? 'Done' : 'Edit tiles'}
                        </button>
                        : null}
                </div>
                {hint ? <p className="bingo-hint">{hint}</p> : null}
                {canEdit && editingTiles ?
                    <div className="bingo-shape">
                        <span>{rows} rows × {columns} columns</span>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving || rows >= SIDE_MAX}
                            onClick={() => this.change(() => api.bingo.addRow(), 'boardError')}>Add row</button>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving || columns >= SIDE_MAX}
                            onClick={() => this.change(() => api.bingo.addColumn(), 'boardError')}>Add column</button>
                    </div>
                    : null}
                {canEdit && editingTiles && confirmingLine ?
                    <div className="bingo-shape" role="group" aria-label={`Remove ${confirmingLine.kind} ${confirmingLine.index + 1}`}>
                        <span className="bingo-confirm">Remove {confirmingLine.kind} {confirmingLine.index + 1}, with its tiles and who completed them?</span>
                        <button type="button" className="bingo-btn bingo-btn-danger" disabled={saving}
                            onClick={() => this.removeLine(confirmingLine.kind, confirmingLine.index, true)}>Yes, remove</button>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} autoFocus onClick={() => this.setState({ confirmingLine: null })}>Keep it</button>
                    </div>
                    : null}
                {boardError ? <p className="bingo-error" role="alert">{boardError}</p> : null}
                <div className="bingo-board-scroll">
                    {/* while the tiles are being edited, a remove button heads each column and each row */}
                    <div className={'bingo-board' + (editingTiles ? ' bingo-board-editing' : '')}
                        style={{ gridTemplateColumns: (editingTiles ? '26px ' : '') + `repeat(${columns}, minmax(0, 1fr))`, minWidth: columns * 90, maxWidth: columns * 150 }}>
                        {editingTiles ?
                            <>
                                <span />
                                {tiles.slice(0, columns).map((tile, column) => (
                                    <button type="button" key={'column' + column} className="bingo-line-remove" disabled={saving || columns <= 1}
                                        aria-label={`Remove column ${column + 1}`} title={`Remove column ${column + 1}`}
                                        onClick={() => this.removeLine('column', column)}>✕</button>
                                ))}
                            </>
                            : null}
                        {tiles.map((tile, i) => {
                            let rowRemove = editingTiles && i % columns === 0 ?
                                <button type="button" key={'row' + i} className="bingo-line-remove" disabled={saving || rows <= 1}
                                    aria-label={`Remove row ${i / columns + 1}`} title={`Remove row ${i / columns + 1}`}
                                    onClick={() => this.removeLine('row', i / columns)}>✕</button>
                                : null;
                            let done = !editingTiles && completed.includes(i);
                            let blank = !tile.title && !tile.image;
                            let className = 'bingo-tile' + (done ? ' bingo-tile-done' : '') + (blank ? ' bingo-tile-blank' : '');
                            let content = (
                                <>
                                    {showPoints && !blank ? <span className="bingo-tile-points">{tile.points}</span> : null}
                                    {tile.image ? <img className="bingo-tile-image" src={tile.image} alt="" /> : null}
                                    {tile.title || blank ? <span className="bingo-tile-title">{tile.title || (editingTiles ? '+' : '')}</span> : null}
                                    {done ? <span className="bingo-tile-check" aria-hidden="true">✓</span> : null}
                                </>
                            );
                            // tiles are buttons only for someone who can do something by clicking them
                            let shown = canEdit && (editingTiles || team) ?
                                <button type="button" key={i} className={className} aria-pressed={editingTiles ? undefined : done}
                                    aria-label={editingTiles ? `Edit tile ${i + 1}: ${tile.title || (blank ? 'blank' : 'image only')}` : (tile.title ? undefined : `Tile ${i + 1}`)}
                                    onClick={() => this.clickTile(i)}>{content}</button>
                                :
                                <div key={i} className={className}>{content}{done ? <span className="sr-only"> (completed)</span> : null}</div>;
                            return rowRemove ? [rowRemove, shown] : shown;
                        })}
                    </div>
                </div>
            </section>
        );
    }

    // The form for what a tile says, shows and is worth, over the page
    renderTileForm() {
        let { tileOpen, tileTitle, tilePoints, saving, tileError, columns } = this.state;
        let close = () => this.setState({ tileOpen: null });
        return (
            <div className="bingo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { close(); } }}
                onKeyDown={(e) => { if (e.key === 'Escape') { close(); } }}>
                <form className="bingo-tile-form" role="dialog" aria-modal="true" aria-labelledby="bingo-tile-form-title" onSubmit={this.saveTile}>
                    <h2 id="bingo-tile-form-title">Tile {tileOpen + 1} <span>row {Math.floor(tileOpen / columns) + 1}, column {tileOpen % columns + 1}</span></h2>
                    <label htmlFor="bingo-tile-title">Task</label>
                    <textarea id="bingo-tile-title" rows="3" maxLength={TITLE_MAX} autoFocus value={tileTitle} placeholder="e.g. Obtain a dragon pickaxe"
                        onChange={(e) => this.setState({ tileTitle: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') { this.saveTile(e); } }} />
                    {this.renderImagePicker()}
                    <label htmlFor="bingo-tile-points">Points</label>
                    <input id="bingo-tile-points" type="number" min="0" max={POINTS_MAX} step="1" required value={tilePoints}
                        onChange={(e) => this.setState({ tilePoints: e.target.value })} />
                    <div className="bingo-tile-form-actions">
                        <span className="bingo-count">{tileTitle.length} / {TITLE_MAX}</span>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={close}>Cancel</button>
                        <button type="submit" className="bingo-btn bingo-btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    </div>
                    {tileError ? <p className="bingo-error" role="alert">{tileError}</p> : null}
                </form>
            </div>
        );
    }

    renderEvent() {
        if (this.state.loading) {
            return <p className="bingo-status">Loading the board...</p>;
        }
        if (this.state.error) {
            return <p className="bingo-status">Couldn't load the board right now. Please try again later.</p>;
        }
        let scored = this.scored();
        return (
            <>
                {this.renderScores(scored)}
                {this.renderBoard(scored)}
                {this.state.canEdit && this.state.tileOpen !== null ? this.renderTileForm() : null}
            </>
        );
    }

    // The clomp login, at the top of the page: who is logged in and whether they can edit, or a way to log in
    renderAccount() {
        let { account, accountChecked, canEdit } = this.state;
        if (!accountChecked) {
            return <div className="bingo-account" />;
        }
        return (
            <div className="bingo-account">
                {account ?
                    <>
                        <span className="bingo-account-who">Logged in as <strong>{account.username}</strong></span>
                        <span className={'bingo-account-role' + (canEdit ? ' is-admin' : '')}>{canEdit ? 'Admin' : 'View only'}</span>
                        <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" onClick={this.logOut}>Log out</button>
                    </>
                    :
                    <>
                        <span className="bingo-account-who">Running the event? Log in with an Admin account to edit it.</span>
                        <Link className="bingo-btn bingo-btn-primary bingo-btn-small" to="/login?next=/bingo">Log in</Link>
                    </>}
            </div>
        );
    }

    render() {
        return (
            <div className="bingo-page">
                {this.renderAccount()}
                <div className="bingo-header">
                    <h1>Frog Bingo</h1>
                    {this.state.loading || this.state.error ? null : this.renderTimer()}
                </div>
                {this.renderEvent()}
            </div>
        );
    }

}
